import React from "react";
import AdminNavbar from "../Navbars/AdminNavbar.js";
import PortalSidebar from "./PortalSidebar";
import { PortalApis } from "../../apis/PortalApis";
import { Oval } from 'react-loader-spinner';
import { FaChartBar } from "react-icons/fa";
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);


export default function PortalUsage() {

    const [usage, setUsage] = React.useState<any>(null);
    const [daily, setDaily] = React.useState<any[]>([]);
    const [period, setPeriod] = React.useState<number>(30);
    const [loading, setLoading] = React.useState<boolean>(true);

    React.useEffect(() => { loadUsage(); }, [period]);

    const loadUsage = () => {
        setLoading(true);
        Promise.all([
            PortalApis.getUsage(period),
            PortalApis.getUsageDaily(period),
        ]).then(([usageRes, dailyRes]: any[]) => {
            setLoading(false);
            if (usageRes?.data?.data) setUsage(usageRes.data.data);
            if (dailyRes?.data?.data) setDaily(dailyRes.data.data || []);
        }).catch(() => setLoading(false));
    };

    const chartData = {
        labels: daily.map((d: any) => d.date),
        datasets: [
            { label: 'Requests', data: daily.map((d: any) => d.total), borderColor: '#E04343', backgroundColor: '#F9E8E899', tension: 0.3 },
            { label: 'Errors', data: daily.map((d: any) => d.errors || 0), borderColor: '#F29339', backgroundColor: '#FFDDBD99', tension: 0.3 },
        ],
    };

    return (
        <>
            <PortalSidebar title="Usage" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="API Usage" />
                <div className='lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen'>

                    <div className="flex justify-between items-center lg:pt-5 pt-3">
                        <div>
                            <h3 className="text-[20px] font-semibold">API Usage</h3>
                            <p className="text-[13px] font-normal text-[#999999]">Monitor your API request volume and performance</p>
                        </div>
                        <select value={period} onChange={(e) => setPeriod(parseInt(e.target.value))}
                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] pl-4 p-2.5">
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                        </select>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-16">
                            <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                        </div>
                    ) : (
                        <>
                            {/* Stats */}
                            <div className="grid lg:grid-cols-4 grid-cols-2 gap-4 mt-6">
                                {[
                                    { label: 'Total Requests', value: (usage?.total_requests || 0).toLocaleString(), bg: 'bg-[#EBEBEB66]', text: 'text-[#263238]', border: 'border-[#DADADA]' },
                                    { label: 'Successful', value: (usage?.successful || 0).toLocaleString(), bg: 'bg-[#A1E3CB33]', text: 'text-[#0EBC93]', border: 'border-[#0EBC93]' },
                                    { label: 'Errors', value: (usage?.errors || 0).toLocaleString(), bg: 'bg-[#E3C5A133]', text: 'text-[#F29339]', border: 'border-[#F29339]' },
                                    { label: 'Avg Response', value: `${usage?.avg_response_time || 0}ms`, bg: 'bg-[#CAD8FA]', text: 'text-[#4979ED]', border: 'border-[#4979ED]' },
                                ].map((stat) => (
                                    <div key={stat.label} className={`${stat.bg} ${stat.text} p-[10px] flex-col justify-center items-center border ${stat.border} rounded-[15px]`}>
                                        <div className="flex justify-center"><FaChartBar size={20} /></div>
                                        <h4 className="text-[13px] text-center mt-2">{stat.label}</h4>
                                        <h1 className="text-[28px] font-semibold text-center">{stat.value}</h1>
                                    </div>
                                ))}
                            </div>

                            {/* Chart */}
                            <div className="bg-white rounded-[10px] border border-[#E2E8F0] p-6 mt-6">
                                <h3 className="text-[16px] font-semibold mb-4">Request Volume (Last {period} days)</h3>
                                {daily.length > 0 ? (
                                    <Line data={chartData} options={{ responsive: true, plugins: { legend: { position: 'top' } } }} />
                                ) : (
                                    <p className="text-center py-10 text-[#808080]">No usage data for this period</p>
                                )}
                            </div>

                            {/* Endpoint Breakdown */}
                            {usage?.endpoint_breakdown && usage.endpoint_breakdown.length > 0 && (
                                <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-6 py-5 mt-6">
                                    <h3 className="text-[16px] font-semibold mb-4">Top Endpoints</h3>
                                    <div className="relative overflow-x-auto">
                                        <table className="w-full text-sm text-left text-gray-500">
                                            <thead className="text-[12px] text-[#808080] bg-[#FAFAFA]">
                                                <tr>
                                                    <th className="px-4 py-2">Endpoint</th>
                                                    <th className="px-4 py-2">Method</th>
                                                    <th className="px-4 py-2">Requests</th>
                                                    <th className="px-4 py-2">Avg Time</th>
                                                    <th className="px-4 py-2">Error Rate</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {usage.endpoint_breakdown.map((ep: any, i: number) => (
                                                    <tr key={i} className="bg-white border-b">
                                                        <td className="px-4 py-3 font-mono text-[12px]">{ep.endpoint}</td>
                                                        <td className="px-4 py-3">
                                                            <b style={{ fontWeight: "500", fontSize: '12px', backgroundColor: ep.method === 'GET' ? '#C9F0D0' : '#FFDDBD', color: ep.method === 'GET' ? '#51CF66' : '#FF922B', borderRadius: '10px', padding: '2px 10px' }}>{ep.method}</b>
                                                        </td>
                                                        <td className="px-4 py-3">{(ep.count || 0).toLocaleString()}</td>
                                                        <td className="px-4 py-3">{ep.avg_time || 0}ms</td>
                                                        <td className="px-4 py-3">{ep.error_rate || 0}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}